import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { usersApi } from '../api/client';
import SocialLoginButton from '../components/SocialLoginButton';
import './Auth.css';

const SignUp: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    password: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (error) setError(null);
  };

  const validateForm = () => {
    if (!formData.username.trim()) {
      setError('Username is required');
      return false;
    }
    
    if (formData.username.length < 3) {
      setError('Username must be at least 3 characters long');
      return false;
    }
    
    if (!/^[a-zA-Z0-9_]+$/.test(formData.username)) {
      setError('Username can only contain letters, numbers, and underscores');
      return false;
    }

    if (!formData.email.trim()) {
      setError('Email is required');
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError('Please enter a valid email address');
      return false;
    }

    if (!formData.firstName.trim()) {
      setError('First name is required');
      return false;
    }

    if (!formData.dateOfBirth) {
      setError('Date of birth is required');
      return false;
    }

    // Validate age (must be at least 13 years old)
    const birthDate = new Date(formData.dateOfBirth);
    const today = new Date();
    const age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    const dayDiff = today.getDate() - birthDate.getDate();
    const actualAge = monthDiff < 0 || (monthDiff === 0 && dayDiff < 0) ? age - 1 : age;

    if (actualAge < 13) {
      setError('You must be at least 13 years old to create an account');
      return false;
    }

    if (!formData.password) {
      setError('Password is required');
      return false;
    }

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters long');
      return false;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setLoading(true);
    setError(null);

    try {
      const userData = {
        username: formData.username.toLowerCase(),
        email: formData.email.toLowerCase(),
        password: formData.password, // Include password for authentication
        firstName: formData.firstName,
        lastName: formData.lastName || '',
        displayName: `${formData.firstName} ${formData.lastName || ''}`.trim(),
        role: 'user' as const,
        status: 'active' as const,
        profile: {
          bio: '',
          location: '',
          timezone: 'America/New_York',
          language: 'en',
          theme: 'light',
          dateOfBirth: formData.dateOfBirth
        },
        preferences: {
          notifications: {
            email: true,
            push: false,
            sms: false
          },
          privacy: {
            profileVisibility: 'private' as const,
            shareData: false
          },
          dashboard: {
            defaultView: 'overview',
            showStats: true,
            compactMode: false
          }
        },
        gdprConsent: {
          dataProcessing: true,
          marketing: false,
          analytics: false,
          timestamp: new Date().toISOString()
        },
        metadata: {
          source: 'sign-up',
          tags: ['new-user'],
          notes: 'User created via sign-up form'
        }
      };

      await usersApi.createUser(userData);
      setSuccess(true);
      
      // Redirect to sign-in page after successful registration
      setTimeout(() => {
        navigate('/signin', { 
          state: { 
            message: 'Account created successfully! Please sign in.',
            email: formData.email.toLowerCase()
          }
        });
      }, 2000);

    } catch (err: any) {
      console.error('Sign up error:', err);
      if (err.response?.status === 409) {
        setError('Username or email already exists');
      } else if (err.response?.data?.error) {
        setError(err.response.data.error);
      } else {
        setError('Failed to create account. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSocialLogin = (provider: 'google' | 'github' | 'apple') => {
    // Redirect to the backend OAuth endpoint
    const backendUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';
    window.location.href = `${backendUrl}/auth/${provider}`;
  };

  if (success) {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <div className="success-message">
            <div className="success-icon">✅</div>
            <h2>Account Created Successfully!</h2>
            <p>Welcome to SelfServe, {formData.firstName}!</p>
            <p>Redirecting you to sign in...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-header">
          <h1>Create Account</h1>
          <p>Join SelfServe to get started</p>
        </div>

        {/* Social Login Buttons */}
        <div className="social-login-section">
          <SocialLoginButton
            provider="google"
            onClick={() => handleSocialLogin('google')}
            disabled={loading}
          />
          <SocialLoginButton
            provider="github"
            onClick={() => handleSocialLogin('github')}
            disabled={loading}
          />
          <SocialLoginButton
            provider="apple"
            onClick={() => handleSocialLogin('apple')}
            disabled={loading}
          />
        </div>

        <div className="auth-divider">
          <span>or create account with email</span>
        </div>

        <form onSubmit={handleSubmit} className="auth-form">
          {error && (
            <div className="error-message">
              <span className="error-icon">⚠️</span>
              {error}
            </div>
          )}

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="firstName">First Name *</label>
              <input
                type="text"
                id="firstName"
                name="firstName"
                value={formData.firstName}
                onChange={handleInputChange}
                placeholder="Enter your first name"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="lastName">Last Name</label>
              <input
                type="text"
                id="lastName"
                name="lastName"
                value={formData.lastName}
                onChange={handleInputChange}
                placeholder="Enter your last name"
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="username">Username *</label>
            <input
              type="text"
              id="username"
              name="username"
              value={formData.username}
              onChange={handleInputChange}
              placeholder="Choose a username"
              required
            />
            <small className="form-help">
              Only letters, numbers, and underscores allowed. Minimum 3 characters.
            </small>
          </div>

          <div className="form-group">
            <label htmlFor="email">Email Address *</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              placeholder="Enter your email address"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="dateOfBirth">Date of Birth *</label>
            <input
              type="date"
              id="dateOfBirth"
              name="dateOfBirth"
              value={formData.dateOfBirth}
              onChange={handleInputChange}
              max={new Date().toISOString().split('T')[0]}
              required
            />
            <small className="form-help">
              You must be at least 13 years old to create an account
            </small>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="password">Password *</label>
              <input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                placeholder="Create a password"
                required
              />
              <small className="form-help">
                Minimum 6 characters
              </small>
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword">Confirm Password *</label>
              <input
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleInputChange}
                placeholder="Confirm your password"
                required
              />
            </div>
          </div>

          <button
            type="submit"
            className="auth-button"
            disabled={loading}
          >
            {loading ? (
              <>
                <span className="loading-spinner"></span>
                Creating Account...
              </>
            ) : (
              'Create Account'
            )}
          </button>
        </form>

        <div className="auth-footer">
          <p>
            Already have an account?{' '}
            <Link to="/signin" className="auth-link">
              Sign In
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default SignUp;